import LegalPage, { Section } from "@/components/LegalPage";

export default function Terms() {
  return (
    <LegalPage
      title="Terms of Service"
      metaTitle="AleaEdge Terms of Service"
      metaDescription="The terms governing your use of AleaEdge — informational sports analysis only, no guarantees, account responsibilities, subscriptions, and limitation of liability."
      canonical="/terms"
      intro="These Terms of Service ('Terms') govern your access to and use of AleaEdge. By creating an account or using the service, you agree to these Terms. If you do not agree, do not use AleaEdge."
    >
      <Section title="1. What AleaEdge Is — and Is Not">
        <p>AleaEdge provides sports analysis, statistical models, and AI-generated commentary for informational and entertainment purposes only. AleaEdge is <span className="text-foreground font-medium">not</span> a sportsbook, broker, advisor, or financial service. We do not accept wagers, hold funds, or guarantee outcomes.</p>
        <p>Nothing in the service constitutes financial, investment, or professional advice. Sports outcomes are inherently uncertain. Past performance of any pick, model, or analyst does not guarantee future results.</p>
      </Section>

      <Section title="2. Eligibility">
        <p>You must be at least 18 years old, and at least 21 in jurisdictions where 21 is the legal gambling age, to use AleaEdge. You are responsible for confirming that your use of the service is lawful where you live.</p>
      </Section>

      <Section title="3. Accounts">
        <p>You agree to provide accurate information, keep your credentials confidential, and notify us promptly of any unauthorized access. You are responsible for all activity under your account. One human, one account.</p>
      </Section>

      <Section title="4. Acceptable Use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Resell, redistribute, scrape, or republish AleaEdge content without written permission.</li>
          <li>Reverse-engineer, decompile, or attempt to extract source code or models.</li>
          <li>Use the service to violate any law or third-party right.</li>
          <li>Interfere with the operation of the service, attempt unauthorized access, or probe for vulnerabilities outside a coordinated disclosure process.</li>
          <li>Share access credentials with others.</li>
        </ul>
      </Section>

      <Section title="5. Intellectual Property">
        <p>All content, software, models, branding, picks, articles, and analysis are owned by AleaEdge or its licensors and are protected by intellectual-property laws. We grant you a limited, non-exclusive, non-transferable license to use the service for personal, non-commercial purposes during the term of your subscription.</p>
      </Section>

      <Section title="6. AI-Generated Analysis">
        <p>Portions of AleaEdge content are generated or assisted by large language models. AI-generated text may contain inaccuracies, omissions, or stale information. You should treat all AI output as one input among many and not as a guarantee of any outcome.</p>
      </Section>

      <Section title="7. Subscriptions and Purchases">
        <p>Subscriptions are billed in advance on a recurring basis through Stripe and renew automatically until cancelled. One-time parlay purchases grant access to the specified content only. Detailed billing terms are in the <a href="/billing-terms" className="text-primary hover:underline">Billing Terms</a> and <a href="/refund-policy" className="text-primary hover:underline">Refund Policy</a>.</p>
      </Section>

      <Section title="8. Cancellation and Termination">
        <p>You may cancel your subscription at any time from your account. We may suspend or terminate accounts that violate these Terms, that we reasonably suspect of fraudulent activity, or where required by law. We will give reasonable notice when practical.</p>
      </Section>

      <Section title="9. Disclaimers">
        <p>The service is provided "as is" and "as available", without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, accuracy, and non-infringement. AleaEdge does not warrant that picks will win, that the service will be uninterrupted, or that any defects will be corrected.</p>
      </Section>

      <Section title="10. Limitation of Liability">
        <p>To the maximum extent permitted by law, AleaEdge and its operators will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for lost profits, revenue, data, or goodwill, arising out of or related to your use of the service, even if advised of the possibility of such damages. Our aggregate liability for any claim is limited to the amount you paid AleaEdge in the twelve months preceding the claim.</p>
      </Section>

      <Section title="11. Indemnification">
        <p>You agree to indemnify and hold AleaEdge harmless from any claim or demand arising from your breach of these Terms, your misuse of the service, or your violation of any law or third-party right.</p>
      </Section>

      <Section title="12. Governing Law">
        <p>These Terms are governed by the laws of the State of Delaware, USA, without regard to conflict-of-law rules. Disputes will be resolved in the state or federal courts located in Delaware, except where applicable consumer-protection law provides otherwise.</p>
      </Section>

      <Section title="13. Changes to These Terms">
        <p>We may update these Terms from time to time. Material changes will be announced by email or in-product notice. Your continued use after the effective date constitutes acceptance.</p>
      </Section>

      <Section title="14. Contact">
        <p>Questions about these Terms: <a href="mailto:support@aleaedge.com" className="text-primary hover:underline">support@aleaedge.com</a>.</p>
      </Section>
    </LegalPage>
  );
}
